import React, { useCallback, useState, useEffect, FC } from 'react';
import { useDispatch } from 'react-redux';
import NAV_DATA from '@zola-helpers/client/dist/es/constants/navConstants';
import type { UserContext } from '@zola-helpers/client/dist/es/@types';
import _isEmpty from 'lodash/isEmpty';
import cx from 'classnames';

import NavSecondary from 'components/navV2/NavSecondary';
import NavScrollContainer from 'components/navV2/NavScrollContainer';

import useNavV1Methods from 'components/navV2/useNavV1Methods';
import useNavData from 'components/navV2/useNavData';

import { UniversalTabs } from 'reducers/nav';
import type { WStoreNavView } from '@zola/svc-web-api-ts-client';
import { setActiveUniversalTab } from 'actions/NavActions';
import homeStorePrimaryNav from '../../navV2/isHomeStore';
import FeatureFlags from '../../../util/featureFlags';

import '../../navV2/navV2.less';
import { NavV2MapDispatchProps, NavV2MapStateProps } from './DesktopWrapper';
import DesktopContainer from './DesktopContainer';
import MultiAnnouncementBanner from '../MultiAnnouncementBanner/MultiAnnouncementBanner';

const PublicRegistryNavContainer = React.lazy(() =>
  import(/* webpackChunkName: "public-registry-nav" */ 'components/publicRegistryNav')
);
const PublicRegistryNavV2 = React.lazy(() =>
  import(/* webpackChunkName: "public-registry-nav" */ 'components/publicRegistryNav/v2')
);

const {
  EVENTS: { PUBLIC_REGISTRY_EVENT },
} = NAV_DATA;

// event data to be received from initNavData for setting home store tab as default when loading

type NavV2Props = NavV2MapStateProps & NavV2MapDispatchProps;

const NavV2: FC<NavV2Props> = ({
  addUserContextToRedux,
  maybeFetchUser,
  fetchCategories,
  fetchWeddingShopCategories,
  userContext,
  activeUniversalTab,
}) => {
  const [isPublicRegistry, setIsPublicRegistry] = useState(false);
  const [isPublicRegistryV2, setIsPublicRegistryV2] = useState(false); // TODO: to be removed when the new UI is released
  const [homeStoreNav, setHomeStoreNav] = useState<WStoreNavView>({ categories: [] });
  const dispatch = useDispatch();
  const userContextExists = userContext && !_isEmpty(userContext);

  useEffect(() => {
    const handleSetPublicRegistry = (event: Event) => {
      if ((event as CustomEvent)?.detail === 'EXIT') {
        setIsPublicRegistry(false);
        setIsPublicRegistryV2(false);
      }
      if ((event as CustomEvent)?.detail === 'ENTER_V2') {
        setIsPublicRegistryV2(true);
      } else {
        setIsPublicRegistry(true);
      }
    };
    window.addEventListener(PUBLIC_REGISTRY_EVENT, handleSetPublicRegistry);
    return function cleanup() {
      window.removeEventListener(PUBLIC_REGISTRY_EVENT, handleSetPublicRegistry);
    };
  }, [isPublicRegistry, isPublicRegistryV2, setIsPublicRegistry, setIsPublicRegistryV2]);

  // useCallback so the referential check in useNavData's useEffect works as expected
  const onInitNavData = useCallback(
    (passedUserContext, defaultTab) => {
      if (defaultTab === 'HOME_STORE') {
        dispatch(setActiveUniversalTab(UniversalTabs.HOME_STORE));
      }
      if (!_isEmpty(passedUserContext)) addUserContextToRedux(passedUserContext);
    },
    [addUserContextToRedux, dispatch]
  );

  const {
    showMessage,
    // primaryNavData,
    secondaryNavData,
    primaryLinkId,
    secondaryLinkId,
    disablePrimaryCollapse,
    disablePromoCollapse,
    emitDirectionEvent,
  } = useNavData(onInitNavData, userContext, activeUniversalTab);
  // Extracted hook for V1 support
  useNavV1Methods({
    maybeFetchUser,
    fetchCategories,
    fetchWeddingShopCategories,
  });

  const isHomeStoreActive = activeUniversalTab === UniversalTabs.HOME_STORE;

  const navContainerClasses = cx('navV3__container', {
    'nav-container--blue-background': isHomeStoreActive,
  });

  // the dropdowns for NavPrimary and NavSecondary look different
  // the most efficient and minimal changes for home store nav:
  // hide primary nav
  // add carets to secondary nav
  // just style the css so secondary nav looks like primary nav
  const returnSecondaryNavLinks = (showHomeStoreLinks: boolean) => {
    if (showHomeStoreLinks && homeStoreNav?.categories) {
      return homeStoreNav.categories.length > 0
        ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
          homeStorePrimaryNav(homeStoreNav as any, userContext as UserContext)
        : [];
    }
    // eslint-disable-next-line consistent-return
    return secondaryNavData;
  };

  if (!userContextExists) {
    maybeFetchUser();
  }

  useEffect(() => {
    if (isHomeStoreActive && homeStoreNav?.categories?.length === 0) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      fetchCategories('STORE').then(setHomeStoreNav);
    }
  }, [fetchCategories, homeStoreNav, isHomeStoreActive, setHomeStoreNav]);

  useEffect(() => {
    if (!isPublicRegistry) {
      const classUnifiedNav = document.getElementsByClassName('unified-nav');
      const classHeaderNavRedesign = document.getElementsByClassName('site-header--navRedesign');
      const idNavContainerSSG = document.getElementById('navContainerSSG');
      const finalNavContainers = [...classUnifiedNav, ...classHeaderNavRedesign, idNavContainerSSG];

      let height = 189;
      if (!secondaryNavData || secondaryNavData.length === 0) {
        height = 133;
      }

      finalNavContainers.forEach((element) => {
        if (element) {
          const element2 = element as HTMLElement;
          // eslint-disable-next-line no-param-reassign
          element2.style.height = `${height}px`;
        }
      });

      // class unified-nav web-wedding web-registry web-marketplace
      // id header web-zola or class site-header--navRedesign
      // id navContainerSSG web-preauth
    }
  }, [isPublicRegistry, showMessage, secondaryNavData]);

  if (isPublicRegistry) return <PublicRegistryNavContainer />;
  if (FeatureFlags.get('registryNewPublicStandalone') && isPublicRegistryV2)
    return <PublicRegistryNavV2 />;

  return (
    <NavScrollContainer
      activeLinkId={primaryLinkId}
      disablePrimaryCollapse={disablePrimaryCollapse}
      disablePromoCollapse={disablePromoCollapse}
      emitDirectionEvent={emitDirectionEvent}
    >
      <nav className={navContainerClasses}>
        <MultiAnnouncementBanner showPreauthLinks leftAlignBanners isPostAuth={false} />

        <DesktopContainer activeLinkId={primaryLinkId} />
        <NavSecondary
          secondaryNavData={returnSecondaryNavLinks(isHomeStoreActive)}
          activeLinkId={secondaryLinkId}
        />
      </nav>
    </NavScrollContainer>
  );
};

export default NavV2;
